import { useState, useEffect, useCallback, useRef } from "react";
import "./index.css";
import PixelScene from "./PixelScene";
import HenIntro from "./HenIntro";
import { prefersReducedMotion } from "./intro";

const EMAIL = "kartikmehta202@gmail.com";

const experience = {
  selfLabs: { url: "https://self.xyz", year: "May 2026 - Present" },
  rtCamp: { url: "https://rtcamp.com", year: "Jan 2024 - Apr 2026" },
  aixblock: { url: "https://aixblock.io", year: "Aug 2025 - Sep 2025" },
  push: { url: "https://push.org", year: "Jun 2023 - Dec 2023" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "May 2026" -> month index since year 0; "Present" -> current month
const toMonthIndex = (label) => {
  if (label === "Present") {
    const now = new Date();
    return now.getFullYear() * 12 + now.getMonth();
  }
  const [mon, yr] = label.split(" ");
  return Number(yr) * 12 + MONTHS.indexOf(mon);
};

// Inclusive range, so "Jun 2023 - Dec 2023" counts as 7 months
const toRange = (year) => {
  const [start, end] = year.split(" - ").map(toMonthIndex);
  return [start, end + 1];
};

const monthsIn = (year) => {
  const [start, end] = toRange(year);
  return end - start;
};

// Merge overlapping roles so concurrent work isn't counted twice
const totalExperienceMonths = (items) => {
  const ranges = items.map((item) => toRange(item.year)).sort((a, b) => a[0] - b[0]);
  let total = 0;
  let [curStart, curEnd] = ranges[0];
  for (const [start, end] of ranges.slice(1)) {
    if (start <= curEnd) {
      curEnd = Math.max(curEnd, end);
    } else {
      total += curEnd - curStart;
      [curStart, curEnd] = [start, end];
    }
  }
  return total + (curEnd - curStart);
};

// e.g. 28 -> "2 years and 4 months", 5 -> "5 months"
const formatDuration = (months) => {
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  const parts = [];
  if (yrs) parts.push(`${yrs} ${yrs === 1 ? "year" : "years"}`);
  if (mos || !yrs) parts.push(`${mos} ${mos === 1 ? "month" : "months"}`);
  return parts.join(" and ");
};

const sentOn = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const links = {
  blog: "https://writer.mrmehta.in",
  book: "https://www.amazon.in/dp/B0GTPX92DB/",
  pocket: "https://www.pocket-app.xyz/",
  nt: "https://www.agent-lang.xyz/",
  penguin: "https://www.trypenguin.xyz",
};

const Ext = ({ href, children, ...rest }) => (
  <a
    href={href}
    {...(href.startsWith("http") && {
      target: "_blank",
      rel: "noopener noreferrer",
    })}
    {...rest}
  >
    {children}
  </a>
);

// Brand marks from Simple Icons (CC0) and a filled mail glyph, drawn in currentColor
const contacts = [
  {
    label: "Book a call",
    href: "https://calendly.com/kartikmehta/15min",
    path: "M5.53 2.13 0 7.75h5.53zm.398 0v5.62h7.608v3.65l5.47-4.45c-.014-1.22.031-2.25-.025-3.46-.148-1.09-1.287-1.47-2.236-1.36zM23.1 4.32c-.802.295-1.358.995-2.047 1.49-2.506 2.05-4.982 4.12-7.468 6.19 3.025 2.59 6.04 5.18 9.065 7.76 1.218.671 1.428-.814 1.328-1.64v-13a.828.828 0 0 0-.877-.825zM.038 8.15v7.7h5.53v-7.7zm13.577 8.1H6.008v5.62c3.864-.006 7.737.011 11.58-.009 1.02-.07 1.618-1.12 1.468-2.07v-2.51l-5.47-4.68v3.65zm-13.577 0c.02 1.44-.041 2.88.033 4.31.162.948 1.158 1.43 2.047 1.31h3.464v-5.62z",
  },
  {
    label: "Send an email",
    href: `mailto:${EMAIL}?subject=${encodeURIComponent("Hi Kartik")}`,
    path: "M20 8l-8 5-8-5V6l8 5 8-5v2zm0-4H4c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6c0-1.11-.9-2-2-2z",
  },
  {
    label: "Twitter",
    href: "https://x.com/kartik_mehta8",
    path: "M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z",
  },
  {
    label: "GitHub",
    href: "https://github.com/kartikmehta8",
    path: "M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/kartikmehta17",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z",
  },
  {
    label: "Telegram",
    href: "https://t.me/mehtaaaaaah",
    path: "M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z",
  },
];

function App() {
  // Opening: "waiting" for the hen sprite, then "intro", then "done"
  const [stage, setStage] = useState(() => (prefersReducedMotion() ? "done" : "waiting"));
  const clock = useRef({ start: stage === "done" ? -Infinity : null }).current;
  const [hen, setHen] = useState(null);

  const onIntroDone = useCallback(() => setStage("done"), []);

  const onHen = useCallback(
    (info) => {
      setHen(info);
      if (clock.start === null) {
        clock.start = performance.now();
        setStage("intro");
      }
    },
    [clock]
  );

  // If the hen never loads, don't leave the page blank
  useEffect(() => {
    const id = setTimeout(() => {
      if (clock.start === null) {
        clock.start = -Infinity;
        setStage("done");
      }
    }, 2500);
    return () => clearTimeout(id);
  }, [clock]);

  const { selfLabs, rtCamp, aixblock, push } = experience;

  return (
    <div className="postcard">
      <div className="scene">
        <PixelScene clock={clock} onHen={onHen} />
      </div>

      <main className={`mail${stage === "waiting" ? " is-waiting" : ""}`}>
        <h1 className="subject">Hi, I'm Kartik</h1>

        <div className="sender">
          <img className="avatar" src={`${process.env.PUBLIC_URL}/hen.png`} alt="" />
          <div className="sender-info">
            <p>
              <strong>Kartik Mehta</strong>{" "}
              <span className="muted">&lt;{EMAIL}&gt;</span>
            </p>
            <p className="muted">to you</p>
          </div>
          <span className="muted sent">{sentOn}</span>
        </div>

        <div className="body">
          <p>Hi there,</p>

          <p>
            I build software and ship products. I'm an engineer, creator and
            author, and right now I'm a Solutions Engineer at{" "}
            <Ext href={selfLabs.url}>Self Labs</Ext>, working across agentic
            systems and zero-knowledge cryptography.
          </p>

          <p>
            Before that I was a Software Engineer at{" "}
            <Ext href={rtCamp.url}>rtCamp</Ext> for{" "}
            {formatDuration(monthsIn(rtCamp.year))}, a Technical Writer at{" "}
            <Ext href={aixblock.url}>AIxBlock</Ext>, and a Developer at{" "}
            <Ext href={push.url}>Push Chain</Ext>. That's{" "}
            {formatDuration(totalExperienceMonths(Object.values(experience)))} in
            total.
          </p>

          <p>
            These days I'm building tools for AI agents.{" "}
            <Ext href={links.pocket}>Pocket</Ext> gives an agent its own wallet with
            a hard spending cap, <Ext href={links.nt}>NT</Ext> is a language for
            agent ecosystems, and <Ext href={links.penguin}>Penguin</Ext> is an
            email editor built for AI agents.
          </p>

          <p>
            I also wrote a book,{" "}
            <Ext href={links.book}>The Manifesto of Shipping Anyway</Ext>, and I
            write on <Ext href={links.blog}>my blog</Ext>.
          </p>

          <p>Best,</p>

          <div className="signature">
            <p>--</p>
            <p className="signature-name">Kartik Mehta</p>
            <p>solutions engineer</p>
            <p className="quote">"How you do anything is how you do everything."</p>
          </div>
        </div>

        <div className="actions">
          {contacts.map(({ label, href, path }) => (
            <Ext key={label} className="chip" href={href} aria-label={label} title={label}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
                <path d={path} />
              </svg>
            </Ext>
          ))}
        </div>
      </main>

      {hen && stage === "intro" && <HenIntro hen={hen} clock={clock} onDone={onIntroDone} />}
    </div>
  );
}

export default App;
